import type { IntelligenceRequest } from '@shared/domain/intelligenceRequest/IntelligenceRequest';
import { Avatar, Box, Checkbox, Chip, Tooltip, Typography } from '@mui/material';
import { AnimatePresence } from 'framer-motion';
import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Chevron,
  InfoBar,
  RequestsHeader,
  SectionSubtext,
  SectionTitle,
} from './review_forms/ReviewForm.styles';

interface ActiveReviewIntelligenceRequestsProps {
  companyId: number;
  extendReview: (args: { intelligenceRequestId: number }) => void;
  selectedId: number | null;
  setSelectedId: (id: number | null) => void;
  intelligenceRequests: IntelligenceRequest[];
}

const ActiveReviewIntelligenceRequests: React.FC<
  ActiveReviewIntelligenceRequestsProps
> = ({
  intelligenceRequests,
  companyId: _companyId,
  extendReview,
  selectedId,
  setSelectedId,
}) => {
  const [searchParams] = useSearchParams();
  const [showRequests, setShowRequests] = useState(true);

  useEffect(() => {
    const idParam = searchParams.get('intelligenceRequestId');
    if (idParam) {
      const id = Number(idParam);
      if (!Number.isNaN(id)) {
        setShowRequests(true);
        setSelectedId(id);
      }
    }
  }, [searchParams]);

  useEffect(() => {
    if (selectedId !== null) {
      extendReview({ intelligenceRequestId: selectedId });
    }
  }, [selectedId, extendReview]);

  if (intelligenceRequests.length === 0) {
    return null;
  }

  return (
    <InfoBar>
      <RequestsHeader onClick={() => setShowRequests(!showRequests)}>
        <SectionTitle>🎯 Active Intelligence Requests 💰</SectionTitle>
        <Chevron $open={showRequests} />
      </RequestsHeader>
      <AnimatePresence initial={false}>
        {showRequests && (
          <>
            <SectionSubtext>
              Please select an intelligence request using the checkbox below and
              scope your review to its specific requirements to qualify for the
              cash reward. Once your submission is approved by our admins, you
              could earn part of the cash reward.
            </SectionSubtext>

            {intelligenceRequests.map((req: IntelligenceRequest) => {
              const isSelected = selectedId === Number(req.id);
              return (
                <Box
                  key={req.id}
                  sx={{
                    width: '100%',
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    p: 2,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1.5,
                    mb: 2,
                    backgroundColor: '#ffffff',
                  }}
                >
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                  >
                    <Box display="flex" alignItems="center" gap={2}>
                      <Avatar
                        sx={{ width: 64, height: 64 }}
                        src={`https://logo.clearbit.com/${
                          (req.company?.name
                            ?.replace(/\s+/g, '')
                            .toLowerCase()
                            ?? 'default')
                          || 'default'
                        }.com`}
                        alt={req.company?.name}
                      />
                      <Typography variant="caption" color="textSecondary">
                        Created on
                        {' '}
                        {new Date(req.createdAt).toLocaleDateString()}
                      </Typography>
                    </Box>
                    <Checkbox
                      checked={isSelected}
                      onChange={() =>
                        setSelectedId(isSelected ? null : Number(req.id))}
                    />
                  </Box>
                  {req.description && (
                    <>
                      <Typography variant="body2" gutterBottom sx={{ fontWeight: 600 }}>
                        Description
                      </Typography>
                      <Typography variant="body2">{req.description}</Typography>
                    </>
                  )}
                  <Box
                    sx={{
                      display: 'flex',
                      gap: 3,
                      mb: 2,
                      flexDirection: { xs: 'column', md: 'row' },
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        flex: 1,
                        gap: 2,
                        flexDirection: { xs: 'column', md: 'row' },
                      }}
                    >
                      <Box display="flex" flexDirection="column">
                        <Typography variant="body2" gutterBottom sx={{ fontWeight: 600 }}>
                          Payment
                        </Typography>
                        <Tooltip
                          title={`This intelligence request pays $${req.amountUsd} USD for an approved ${
                            req.engagementType === 'review'
                              ? 'deep dive'
                              : 'phone consultation'
                          }.`}
                          arrow
                          slotProps={{
                            tooltip: {
                              sx: {
                                bgcolor: '#1e293b',
                                color: '#ffffff',
                                fontSize: '0.875rem',
                                p: 1.5,
                                borderRadius: '8px',
                                maxWidth: '300px',
                              },
                            },
                            arrow: {
                              sx: {
                                color: '#1e293b',
                              },
                            },
                          }}
                        >
                          <Chip
                            label={`$${req.amountUsd}`}
                            size="medium"
                            variant="filled"
                            sx={{
                              bgcolor: '#e8f5e9',
                              color: '#2e7d32',
                              fontWeight: 600,
                              borderRadius: '8px',
                              p: '4px 12px',
                            }}
                          />
                        </Tooltip>
                      </Box>

                      {(req.dealArea ?? []).length > 0 && (
                        <Box display="flex" flexDirection="column" gap={1}>
                          <Typography variant="body2" gutterBottom sx={{ fontWeight: 600 }}>
                            Deal Areas
                          </Typography>
                          <Box
                            sx={{
                              display: 'flex',
                              flexWrap: 'wrap',
                              gap: 1,
                              flexDirection: { xs: 'column', sm: 'row' },
                            }}
                          >
                            {(req.dealArea ?? []).map(area => (
                              <Chip
                                key={area}
                                label={area}
                                size="small"
                                variant="filled"
                                sx={{
                                  bgcolor: '#e3f2fd',
                                  color: '#1565c0',
                                  fontWeight: 600,
                                  borderRadius: '8px',
                                  p: '4px 12px',
                                }}
                              />
                            ))}
                          </Box>
                        </Box>
                      )}

                      {req.dealOutcome && (
                        <Box display="flex" flexDirection="column" gap={1}>
                          <Typography variant="body2" gutterBottom sx={{ fontWeight: 600 }}>
                            Deal Outcome
                          </Typography>
                          <Box
                            sx={{
                              display: 'flex',
                              flexWrap: 'wrap',
                              gap: 1,
                              flexDirection: { xs: 'column', sm: 'row' },
                            }}
                          >
                            <Chip
                              label={
                                req.dealOutcome === 'Yes'
                                  ? 'Deal Won'
                                  : 'Deal Lost'
                              }
                              size="small"
                              variant="filled"
                              sx={{
                                bgcolor: '#e3f2fd',
                                color: '#1565c0',
                                fontWeight: 600,
                                borderRadius: '8px',
                                p: '4px 12px',
                              }}
                            />
                          </Box>
                        </Box>
                      )}
                    </Box>
                  </Box>
                </Box>
              );
            })}
          </>
        )}
      </AnimatePresence>
    </InfoBar>
  );
};

export default ActiveReviewIntelligenceRequests;
